import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, ChevronRight, ChevronDown, ChevronUp, Zap, Car, Bike } from 'lucide-react';

interface Launch {
  name: string;
  brand: string;
  path: string;
  date: Date;
  type: 'ev' | 'car' | 'bike';
  tag: string;
}

// Upcoming launches — next 30 days (keep in sync with ThisWeekLaunches)
const LAUNCHES: Launch[] = [
  {
    name: 'Tata Sierra EV',
    brand: 'Tata',
    path: '/cars/tata/sierra-ev',
    date: new Date(Date.UTC(2026, 4, 19)), // May 19
    type: 'ev',
    tag: 'Price reveal',
  },
  {
    name: 'Honda City Facelift',
    brand: 'Honda',
    path: '/cars/honda/city-facelift-2026-india-launch',
    date: new Date(Date.UTC(2026, 4, 22)), // May 22
    type: 'car',
    tag: 'Launch',
  },
  {
    name: 'Honda ZR-V',
    brand: 'Honda',
    path: '/cars/honda/zr-v-india-2026-launch',
    date: new Date(Date.UTC(2026, 4, 22)), // May 22
    type: 'car',
    tag: 'India debut',
  },
  {
    name: 'Hyundai Stargazer',
    brand: 'Hyundai',
    path: '/cars/hyundai/stargazer',
    date: new Date(Date.UTC(2026, 4, 28)), // May 28
    type: 'car',
    tag: 'Unveil',
  },
  {
    name: 'Ducati Scrambler 2G',
    brand: 'Ducati',
    path: '/bikes/ducati/scrambler-2g-india-2026',
    date: new Date(Date.UTC(2026, 5, 5)), // Jun 5
    type: 'bike',
    tag: 'India launch',
  },
  {
    name: 'Royal Enfield Himalayan 750',
    brand: 'Royal Enfield',
    path: '/bikes/royal-enfield/himalayan-750-india-2026',
    date: new Date(Date.UTC(2026, 5, 12)), // Jun 12
    type: 'bike',
    tag: 'Launch',
  },
];

const TYPE_STYLE = {
  ev: { bg: 'bg-green-500', dot: 'bg-green-400', text: 'text-green-400', Icon: Zap },
  car: { bg: 'bg-cat-cars', dot: 'bg-cat-cars', text: 'text-cat-cars', Icon: Car },
  bike: { bg: 'bg-cat-motorcycles', dot: 'bg-cat-motorcycles', text: 'text-cat-motorcycles', Icon: Bike },
} as const;

const DAY_NAMES = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function getNext30Days(): Date[] {
  const days: Date[] = [];
  const today = new Date();
  for (let i = 0; i < 30; i++) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + i));
    days.push(d);
  }
  return days;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate()
  );
}

function daysUntil(date: Date): number {
  const now = Date.now();
  const diff = date.getTime() - now;
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export function LaunchCalendarWidget() {
  const [collapsed, setCollapsed] = useState(false);

  const today = new Date();
  const thirtyDaysOut = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  const upcoming = LAUNCHES.filter((l) => l.date >= today && l.date <= thirtyDaysOut)
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  if (upcoming.length === 0) return null;

  const days = getNext30Days();
  // Pad start to align with day-of-week
  const startDow = days[0].getUTCDay();

  // Map dates to launch types for dot coloring
  const launchDayMap = new Map<string, Launch['type'][]>();
  for (const l of upcoming) {
    const key = `${l.date.getUTCFullYear()}-${l.date.getUTCMonth()}-${l.date.getUTCDate()}`;
    const arr = launchDayMap.get(key) || [];
    arr.push(l.type);
    launchDayMap.set(key, arr);
  }

  return (
    <div className="rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="flex w-full items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-sky-500/5 to-transparent text-left"
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-500/10 flex-shrink-0">
          <CalendarDays className="h-4 w-4 text-sky-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-text-primary text-sm">Launch Calendar</h3>
          <span className="text-[10px] text-text-tertiary">
            {upcoming.length} {upcoming.length === 1 ? 'launch' : 'launches'} in next 30 days
          </span>
        </div>
        {collapsed ? (
          <ChevronDown className="h-4 w-4 text-text-tertiary flex-shrink-0" />
        ) : (
          <ChevronUp className="h-4 w-4 text-text-tertiary flex-shrink-0" />
        )}
      </button>

      {!collapsed && (
        <>
          {/* Mini calendar grid */}
          <div className="px-3 py-2 border-b border-border-primary">
            <div className="grid grid-cols-7 gap-0.5 mb-1">
              {DAY_NAMES.map((d, i) => (
                <span key={i} className="text-[9px] text-text-tertiary text-center font-medium">
                  {d}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {/* Empty cells for alignment */}
              {Array.from({ length: startDow }).map((_, i) => (
                <div key={`pad-${i}`} className="h-6" />
              ))}
              {days.map((day) => {
                const key = `${day.getUTCFullYear()}-${day.getUTCMonth()}-${day.getUTCDate()}`;
                const types = launchDayMap.get(key);
                const isToday = isSameDay(day, today);

                return (
                  <div
                    key={key}
                    className={`h-6 flex flex-col items-center justify-center rounded ${
                      isToday ? 'bg-sky-500/15 ring-1 ring-sky-500/30' : ''
                    } ${types ? 'font-bold' : ''}`}
                  >
                    <span className={`text-[10px] tabular-nums ${
                      types ? 'text-text-primary' : 'text-text-tertiary'
                    } ${isToday ? 'text-sky-400' : ''}`}>
                      {day.getUTCDate()}
                    </span>
                    {types && (
                      <div className="flex gap-px mt-px">
                        {types.slice(0, 3).map((t, i) => (
                          <span key={i} className={`h-1 w-1 rounded-full ${TYPE_STYLE[t].dot}`} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Launch list */}
          <div className="divide-y divide-border-primary">
            {upcoming.map((launch) => {
              const days = daysUntil(launch.date);
              const style = TYPE_STYLE[launch.type];
              const IconComp = style.Icon;

              return (
                <Link
                  key={launch.path}
                  to={launch.path}
                  className="flex items-center gap-3 px-4 py-2 transition-colors hover:bg-bg-tertiary group"
                >
                  <div className={`flex h-7 w-7 items-center justify-center rounded-lg flex-shrink-0 ${style.bg}/10`}>
                    <IconComp className={`h-3.5 w-3.5 ${style.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-text-primary group-hover:text-primary-400 transition-colors truncate">
                      {launch.name}
                    </p>
                    <p className="text-[10px] text-text-tertiary">{launch.brand} · {launch.tag}</p>
                  </div>
                  <div className="flex flex-col items-end flex-shrink-0">
                    <span className="text-xs font-bold text-sky-400 tabular-nums">{days}d</span>
                    <span className="text-[9px] text-text-tertiary">
                      {launch.date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          <Link
            to="/?tag=launch"
            className="flex items-center justify-center gap-1 border-t border-border-primary py-2.5 text-xs font-medium text-primary-500 hover:text-primary-400 transition-colors"
          >
            All upcoming launches
            <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </>
      )}
    </div>
  );
}
